import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert } from '@/types'
import { formatDistanceToNow } from '@/lib/utils'

interface RecentAlertsCardProps {
  alerts: Alert[]
  isLoading: boolean
  onViewAll: () => void
}

export default function RecentAlertsCard({ alerts, isLoading, onViewAll }: RecentAlertsCardProps) {
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-500'
      case 'high': return 'bg-orange-500'
      case 'medium': return 'bg-yellow-500'
      case 'low': return 'bg-blue-500'
      default: return 'bg-gray-400'
    }
  }

  const getSeverityVariant = (severity: string): 'critical' | 'warning' | 'info' | 'default' => {
    switch (severity) {
      case 'critical': return 'critical'
      case 'high': return 'critical'
      case 'medium': return 'warning'
      case 'low': return 'info'
      default: return 'default'
    }
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Recent Critical Alerts
            <Button variant="ghost" size="sm" disabled>View All</Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-start space-x-3 animate-pulse">
                <div className="w-2 h-2 bg-gray-200 rounded-full mt-2"></div>
                <div className="flex-1 min-w-0 space-y-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                </div>
                <div className="w-16 h-6 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Recent Critical Alerts
          <Button variant="ghost" size="sm" onClick={onViewAll}>
            View All
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {alerts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No recent alerts</p>
          </div>
        ) : (
          <div className="space-y-4">
            {alerts.slice(0, 5).map((alert) => (
              <div key={alert.id} className="flex items-start space-x-3">
                <div className={`w-2 h-2 ${getSeverityColor(alert.severity || alert.priority || 'low')} rounded-full mt-2`}></div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{alert.title}</p>
                  <p className="text-sm text-gray-600 truncate">
                    {alert.equipmentName || 'Unknown Equipment'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDistanceToNow(new Date(alert.createdAt))} ago
                  </p>
                </div>
                <Badge variant={getSeverityVariant(alert.severity || alert.priority || 'low')}>
                  {((alert.severity || alert.priority || 'unknown')).charAt(0).toUpperCase() + (alert.severity || alert.priority || 'unknown').slice(1)}
                </Badge>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}